/*
 * duo-arcade-engine.js  —  Duo Gift Arcade deterministic core
 * ---------------------------------------------------------------------------
 * PURE LOGIC. No DOM, no network, no Date.now(), no Math.random(). Everything
 * is driven by an explicit numeric seed so a round is 100% reproducible from
 * (seed + move log). That is the property that makes future server-side score
 * replay cheap: the Worker runs this exact module on the submitted seed+moves
 * and reconstructs the score, cleared tally, and result bit-for-bit.
 *
 * Runs in the browser (attaches to window.DuoArcadeEngine) and in Node
 * (module.exports) so the same file backs the storefront, the unit tests, and
 * the eventual replay Worker. ONE source of truth.
 *
 * Contents:
 *   1. PRNG (mulberry32) + small color math
 *   2. Archetype + swatch config (the 12 manufacturable colors live here)
 *   3. Match-3 board: deterministic fill, match detection, POWER-UPS (line /
 *      rainbow / bomb), special activation + chained detonations, gravity +
 *      seeded refill, cascade resolution, scoring, per-id clear tally, and a
 *      per-step animation record (cleared / falls / spawns / creations).
 *   4. Deterministic auto-player (for tests + the CDP harness) + replay
 *   5. Color-harvest -> recipient palette (the GATE, used by the door game)
 *
 * POWER-UPS (classic match-3 set; deterministic, replay-safe):
 *   - a run of exactly 4 mints a LINE gem (clears its whole row or column)
 *   - a run of 5+ mints a RAINBOW gem (color-bomb: clears every gem of one id)
 *   - an L / T intersection (a cell shared by a horizontal AND a vertical run)
 *     mints a BOMB gem (clears a 3x3 area)
 *   Specials are stored in a parallel `specials` array so `cells` stays a flat
 *   list of plain ids (the recommender + tally read ids only, untouched). They
 *   are never created by rng, only by board shape, so seed+log -> identical
 *   specials on replay. Swapping a special activates it; a blast that engulfs
 *   another special detonates that one too (chain reaction).
 *
 * POWER-UP COMBOS (v3, the classic striped+wrapped moment): swapping two
 *   specials together merges into a bigger effect instead of two independent
 *   blasts, centered on the destination cell of the gesture:
 *     line + line  -> a full cross (whole row AND whole column)
 *     line + bomb  -> a thick cross (3 full rows AND 3 full columns)
 *     bomb + bomb  -> a 5x5 blast
 *     rainbow + X  -> unchanged (color-target / whole board), see below
 *   Pure board reads, no rng, so replay stays bit-for-bit. Deterministic
 *   iteration everywhere (numeric cell order).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.DuoArcadeEngine = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  /* ===================================================================== *
   * 1. PRNG + color math
   * ===================================================================== */

  // mulberry32: tiny, fast, well-distributed 32-bit PRNG. Deterministic from
  // a single integer seed; we thread ONE generator through the whole round so
  // the refill stream is reproducible.
  function makeRng(seed) {
    var a = seed >>> 0;
    return function rng() {
      a |= 0; a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // 32-bit string hash -> seed, so a human-friendly seed string ("neon-7")
  // maps to a stable integer.
  function hashSeed(value) {
    var str = String(value);
    var h = 2166136261 >>> 0;
    for (var i = 0; i < str.length; i += 1) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function hexToRgb(hex) {
    var v = String(hex || '').replace('#', '');
    if (v.length === 3) v = v.replace(/./g, function (c) { return c + c; });
    var n = parseInt(v, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  // Relative luminance (WCAG) of a hex color, 0..1.
  function relLuminance(hex) {
    var c = hexToRgb(hex);
    function lin(x) { x /= 255; return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); }
    return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
  }

  // WCAG contrast ratio between two hex colors, 1..21.
  function contrastRatio(hexA, hexB) {
    var la = relLuminance(hexA), lb = relLuminance(hexB);
    var hi = Math.max(la, lb), lo = Math.min(la, lb);
    return (hi + 0.05) / (lo + 0.05);
  }

  // Perceptual-ish color distance (weighted RGB, "redmean"). Cheap and good
  // enough to catch near-duplicate swatches (e.g. cyan vs blue) without a full
  // Lab conversion. ~0..765; ~110 is a sensible "too similar" threshold.
  function colorDistance(hexA, hexB) {
    var a = hexToRgb(hexA), b = hexToRgb(hexB);
    var rmean = (a.r + b.r) / 2;
    var dr = a.r - b.r, dg = a.g - b.g, db = a.b - b.b;
    return Math.sqrt(
      (2 + rmean / 256) * dr * dr +
      4 * dg * dg +
      (2 + (255 - rmean) / 256) * db * db
    );
  }

  /* ===================================================================== *
   * 2. Config: the 12 manufacturable swatches + archetypes
   * ===================================================================== */

  // The 12 real, manufacturable colors. ids + hexes mirror
  // tools/door-print/door-live-options.json (color-4-max). The live snapshot
  // is still the source of truth at runtime; this copy lets the engine + tests
  // run with zero network and guarantees every palette output is one of these.
  var SWATCHES = [
    { id: 'red',           label: 'Red',          hex: '#f01e23', glyph: 'flame'   },
    { id: 'orange',        label: 'Orange',       hex: '#fc5522', glyph: 'bolt'    },
    { id: 'yellow',        label: 'Yellow',       hex: '#f9db1b', glyph: 'star'    },
    { id: 'green',         label: 'Green',        hex: '#12b65f', glyph: 'leaf'    },
    { id: 'cyan',          label: 'Cyan',         hex: '#188df3', glyph: 'wave'    },
    { id: 'blue',          label: 'Blue',         hex: '#1123c5', glyph: 'moon'    },
    { id: 'purple',        label: 'Purple',       hex: '#786af9', glyph: 'diamond' },
    { id: 'magenta',       label: 'Magenta',      hex: '#fe3fa4', glyph: 'heart'   },
    { id: 'pink',          label: 'Pink',         hex: '#fe6a76', glyph: 'blossom' },
    { id: 'silver',        label: 'Silver',       hex: '#9ea4ad', glyph: 'gear'    },
    { id: 'gold',          label: 'Gold',         hex: '#ed9f48', glyph: 'crown'   },
    { id: 'black-sparkle', label: 'Black Sparkle', hex: '#2b2c2f', glyph: 'sparkle' }
  ];

  var SWATCH_BY_ID = {};
  SWATCHES.forEach(function (s) { SWATCH_BY_ID[s.id] = s; });

  // Archetypes. Phase 0.5 ships ONE (neon-maximalist) but the shape is the
  // locked 5-archetype set so Phase 1 just fills the rest in.
  //   gemPool  : the 6 colors that appear as gems (5-6 is the match-3 sweet
  //              spot, and confining the board to on-vibe colors is what makes
  //              the harvest feel on-brand no matter how you play).
  //   biasOrder: archetype's preference ranking, used ONLY as a small tiebreak
  //              nudge so the game ordering dominates.
  //   layout   : the door-piece-type value id that seeds the reveal scene.
  var ARCHETYPES = {
    'neon-maximalist': {
      id: 'neon-maximalist',
      name: 'The Neon Maximalist',
      tagline: 'Fills the room, then fills it again.',
      voice: 'loud, joyful, more-is-more',
      gemPool: ['magenta', 'cyan', 'purple', 'yellow', 'green', 'pink'],
      biasOrder: ['magenta', 'cyan', 'purple', 'pink', 'green', 'yellow'],
      layout: 'full-door-4-parts',
      board: { cols: 7, rows: 8 }
    }
    // Phase 1: lan-party-legend, cozy-save-point, y2k-stickerbomb, low-key-minimalist
  };

  /* ===================================================================== *
   * 3. Match-3 board
   * ===================================================================== */

  // Special-gem types. Stored in state.specials (parallel to state.cells).
  //   line-h  : clears its whole ROW    (minted by a horizontal 4-run)
  //   line-v  : clears its whole COLUMN  (minted by a vertical 4-run)
  //   bomb    : clears a 3x3 area        (minted by an L/T intersection)
  //   rainbow : clears every gem of one id (minted by a 5+ run; color-bomb)
  var SPECIAL = { LINE_H: 'line-h', LINE_V: 'line-v', BOMB: 'bomb', RAINBOW: 'rainbow' };
  function isLine(t) { return t === SPECIAL.LINE_H || t === SPECIAL.LINE_V; }

  // Board is a flat array of color ids (length cols*rows), row-major, row 0 at
  // the TOP. Gravity pulls toward higher row indices (down). A "cell" is its
  // flat index; helpers convert to/from [row, col].
  function idx(state, row, col) { return row * state.cols + col; }

  function inBounds(state, row, col) {
    return row >= 0 && row < state.rows && col >= 0 && col < state.cols;
  }

  // The matchable color of a cell. A RAINBOW gem is a wildcard that NEVER
  // matches by color (like a color bomb), so it reports null and can only be
  // used by being swapped/activated. Line + bomb gems still match by their id.
  function matchColorAt(state, i) {
    if (state.cells[i] == null) return null;
    if (state.specials[i] === SPECIAL.RAINBOW) return null;
    return state.cells[i];
  }

  // Fill a fresh board with no pre-existing 3-in-a-rows, so the first move is
  // always meaningful. Deterministic: pulls from the threaded rng only.
  function fillBoard(state) {
    var pool = state.gemPool;
    for (var r = 0; r < state.rows; r += 1) {
      for (var c = 0; c < state.cols; c += 1) {
        var banned = {};
        // avoid completing a horizontal triple
        if (c >= 2) {
          var l1 = state.cells[idx(state, r, c - 1)];
          var l2 = state.cells[idx(state, r, c - 2)];
          if (l1 === l2) banned[l1] = true;
        }
        // avoid completing a vertical triple
        if (r >= 2) {
          var u1 = state.cells[idx(state, r - 1, c)];
          var u2 = state.cells[idx(state, r - 2, c)];
          if (u1 === u2) banned[u1] = true;
        }
        var choices = pool.filter(function (id) { return !banned[id]; });
        var pick = choices[Math.floor(state.rng() * choices.length)] || pool[0];
        state.cells[idx(state, r, c)] = pick;
      }
    }
  }

  // Create a deterministic board state. seed may be a number or a string.
  function createGame(opts) {
    opts = opts || {};
    var archetype = ARCHETYPES[opts.archetypeId] || ARCHETYPES['neon-maximalist'];
    var board = (opts.board || archetype.board);
    var n = board.cols * board.rows;
    var state = {
      version: 'duo-arcade-board-v3',   // v2: power-ups; v3: special+special combos
      seed: typeof opts.seed === 'number' ? (opts.seed >>> 0) : hashSeed(opts.seed != null ? opts.seed : 'duo'),
      archetypeId: archetype.id,
      cols: board.cols,
      rows: board.rows,
      fourWay: !!opts.fourWay,   // orthogonal-only swaps (see adjacent())
      gemPool: (opts.gemPool || archetype.gemPool).slice(),
      cells: [],
      specials: [],       // colorId-parallel; null | line-h | line-v | bomb | rainbow
      rng: null,
      score: 0,
      moves: 0,
      tally: {},          // id -> total gems cleared (incl. cascades + blasts)
      clearedTotal: 0,
      log: []             // applied moves, for replay/audit
    };
    state.gemPool.forEach(function (id) { state.tally[id] = 0; });
    state.cells = new Array(n);
    state.specials = new Array(n);
    for (var si = 0; si < n; si += 1) state.specials[si] = null;
    state.rng = makeRng(state.seed);
    fillBoard(state);
    // Resolve any incidental matches the no-triple fill missed (diagonals
    // never match, but be safe) WITHOUT scoring or minting specials: this is
    // the settled starting board.
    resolve(state, /*scoring*/ false, null, null);
    return state;
  }

  // Find every horizontal/vertical run of >=3 same-color cells. Each run now
  // carries its member cells + orientation so the resolver can mint specials.
  // Returns { marked:{idx:true}, runs:[{ cells:[idx], color, orient:'h'|'v', len }] }.
  function findMatches(state) {
    var marked = {};
    var runs = [];
    var r, c, k;
    // horizontal
    for (r = 0; r < state.rows; r += 1) {
      var hStart = 0;
      for (c = 1; c <= state.cols; c += 1) {
        var prev = matchColorAt(state, idx(state, r, c - 1));
        var cur = c < state.cols ? matchColorAt(state, idx(state, r, c)) : null;
        if (cur != null && cur === prev) continue;
        var hlen = c - hStart;
        if (hlen >= 3 && prev != null) {
          var hc = [];
          for (k = 0; k < hlen; k += 1) { var ci = idx(state, r, hStart + k); hc.push(ci); marked[ci] = true; }
          runs.push({ cells: hc, color: prev, orient: 'h', len: hlen });
        }
        hStart = c;
      }
    }
    // vertical
    for (c = 0; c < state.cols; c += 1) {
      var vStart = 0;
      for (r = 1; r <= state.rows; r += 1) {
        var pv = matchColorAt(state, idx(state, r - 1, c));
        var cv = r < state.rows ? matchColorAt(state, idx(state, r, c)) : null;
        if (cv != null && cv === pv) continue;
        var vlen = r - vStart;
        if (vlen >= 3 && pv != null) {
          var vc = [];
          for (k = 0; k < vlen; k += 1) { var vi = idx(state, vStart + k, c); vc.push(vi); marked[vi] = true; }
          runs.push({ cells: vc, color: pv, orient: 'v', len: vlen });
        }
        vStart = r;
      }
    }
    return { marked: marked, runs: runs };
  }

  // Decide which cells become special gems this resolve step, and of what type.
  //   - a cell shared by a horizontal AND a vertical run -> BOMB (L/T shape)
  //   - any remaining run of 5+ -> RAINBOW
  //   - any remaining run of exactly 4 -> LINE (orientation of the run)
  // The host cell prefers the pivot (the just-swapped cell) when it is in the
  // run, else the run's middle cell, so a special lands where the player acted.
  // Returns { creations:[{idx,type}], consumed:{idx:true} }. Consumed cells are
  // the special hosts and must NOT be cleared (they transform instead).
  function planSpecials(state, runs, pivot) {
    var hAt = {}, vAt = {};
    var i, j;
    for (i = 0; i < runs.length; i += 1) {
      var tbl = runs[i].orient === 'h' ? hAt : vAt;
      for (j = 0; j < runs[i].cells.length; j += 1) tbl[runs[i].cells[j]] = i;
    }
    var creations = [];
    var consumed = {};
    var runUsed = {};
    // 1. Intersections -> bomb. Iterate cells in numeric order for determinism.
    var inter = [];
    for (var key in hAt) { if (vAt[key] != null) inter.push(key | 0); }
    inter.sort(function (a, b) { return a - b; });
    for (i = 0; i < inter.length; i += 1) {
      var ix = inter[i];
      var hi = hAt[ix], vi = vAt[ix];
      if (runUsed[hi] || runUsed[vi]) continue;
      creations.push({ idx: ix, type: SPECIAL.BOMB });
      consumed[ix] = true; runUsed[hi] = true; runUsed[vi] = true;
    }
    // 2. Remaining straight runs -> rainbow (5+) or line (exactly 4).
    for (i = 0; i < runs.length; i += 1) {
      if (runUsed[i]) continue;
      var rn = runs[i];
      var type = null;
      if (rn.len >= 5) type = SPECIAL.RAINBOW;
      else if (rn.len === 4) type = (rn.orient === 'h') ? SPECIAL.LINE_H : SPECIAL.LINE_V;
      if (!type) continue;
      var host = -1;
      if (pivot) {
        for (j = 0; j < rn.cells.length; j += 1) {
          if (rn.cells[j] === pivot[0] || rn.cells[j] === pivot[1]) { host = rn.cells[j]; break; }
        }
      }
      if (host < 0) host = rn.cells[Math.floor(rn.cells.length / 2)];
      creations.push({ idx: host, type: type });
      consumed[host] = true; runUsed[i] = true;
    }
    return { creations: creations, consumed: consumed };
  }

  // The cells a single special clears when it fires from index `i`.
  function blastCells(state, i, type) {
    var out = [];
    var row = Math.floor(i / state.cols), col = i % state.cols;
    var r, c;
    if (type === SPECIAL.LINE_H) { for (c = 0; c < state.cols; c += 1) out.push(idx(state, row, c)); }
    else if (type === SPECIAL.LINE_V) { for (r = 0; r < state.rows; r += 1) out.push(idx(state, r, col)); }
    else if (type === SPECIAL.BOMB) {
      for (r = row - 1; r <= row + 1; r += 1) {
        for (c = col - 1; c <= col + 1; c += 1) {
          if (inBounds(state, r, c)) out.push(idx(state, r, c));
        }
      }
    } else if (type === SPECIAL.RAINBOW) {
      // standalone rainbow (e.g. caught in another blast): clear its own id.
      var col0 = state.cells[i];
      for (var k = 0; k < state.cells.length; k += 1) if (state.cells[k] === col0) out.push(k);
    }
    return out;
  }

  // Expand a set of detonating specials into the full cleared set, chaining
  // through any other specials the blasts engulf. Pure board read, no rng.
  function detonate(state, seeds) {
    var cleared = {};
    var queue = seeds.slice();
    var queued = {};
    seeds.forEach(function (s) { queued[s.idx] = true; cleared[s.idx] = true; });
    while (queue.length) {
      var s = queue.shift();
      var hits = blastCells(state, s.idx, s.type);
      for (var i = 0; i < hits.length; i += 1) {
        var h = hits[i];
        cleared[h] = true;
        if (state.specials[h] && !queued[h]) {
          queued[h] = true;
          queue.push({ idx: h, type: state.specials[h] });
        }
      }
    }
    return cleared;
  }

  // The merged blast of swapping two non-rainbow specials together (the
  // striped+wrapped moment). Centered on `ib`, the cell the gesture landed on
  // (deterministic for replay: applyMove always passes the swap's b cell).
  // Chains through any OTHER special the merged blast engulfs. Pure board
  // read, no rng.
  function comboClears(state, ia, ib) {
    var ta = state.specials[ia], tb = state.specials[ib];
    var set = {};
    var row = Math.floor(ib / state.cols), col = ib % state.cols;
    var r, c;
    function addRow(rr) { if (rr >= 0 && rr < state.rows) for (var cc = 0; cc < state.cols; cc += 1) set[idx(state, rr, cc)] = true; }
    function addCol(cc) { if (cc >= 0 && cc < state.cols) for (var rr = 0; rr < state.rows; rr += 1) set[idx(state, rr, cc)] = true; }
    if (isLine(ta) && isLine(tb)) {
      addRow(row); addCol(col);                    // full cross
    } else if (ta === SPECIAL.BOMB && tb === SPECIAL.BOMB) {
      for (r = row - 2; r <= row + 2; r += 1) {    // 5x5
        for (c = col - 2; c <= col + 2; c += 1) {
          if (inBounds(state, r, c)) set[idx(state, r, c)] = true;
        }
      }
    } else {                                       // line + bomb: thick cross
      addRow(row - 1); addRow(row); addRow(row + 1);
      addCol(col - 1); addCol(col); addCol(col + 1);
    }
    set[ia] = true; set[ib] = true;                // both specials are consumed
    // chain through any other special caught in the merged blast (numeric
    // cell order for determinism)
    var seeds = [];
    var caught = Object.keys(set).map(function (k) { return k | 0; }).sort(function (a, b) { return a - b; });
    for (var i = 0; i < caught.length; i += 1) {
      var ci = caught[i];
      if (ci !== ia && ci !== ib && state.specials[ci]) seeds.push({ idx: ci, type: state.specials[ci] });
    }
    if (seeds.length) { var d = detonate(state, seeds); for (var dk in d) set[dk | 0] = true; }
    return set;
  }

  // The cleared set produced by swapping the two cells when at least one is a
  // special (activation). Covers single specials, special+special combos, and
  // rainbow targeting. Pure board read, no rng.
  function activationClears(state, ia, ib) {
    var sa = state.specials[ia], sb = state.specials[ib];
    var set = {};
    var k;
    function add(i) { set[i] = true; }
    if (sa === SPECIAL.RAINBOW && sb === SPECIAL.RAINBOW) {
      for (k = 0; k < state.cells.length; k += 1) add(k);             // whole board
      return set;
    }
    if (sa === SPECIAL.RAINBOW || sb === SPECIAL.RAINBOW) {
      var rIdx = sa === SPECIAL.RAINBOW ? ia : ib;
      var oIdx = sa === SPECIAL.RAINBOW ? ib : ia;
      var oType = state.specials[oIdx];
      var target = state.cells[oIdx];
      for (k = 0; k < state.cells.length; k += 1) {
        if (state.cells[k] === target && state.specials[k] !== SPECIAL.RAINBOW) add(k);
      }
      add(rIdx); add(oIdx);
      if (oType) { var d = detonate(state, [{ idx: oIdx, type: oType }]); for (var dk in d) add(dk | 0); }
      return set;
    }
    // both are line/bomb specials: the merged combo effect (v3)
    if (sa && sb) return comboClears(state, ia, ib);
    // single line / bomb: fire it.
    var seeds = [];
    if (sa) seeds.push({ idx: ia, type: sa });
    if (sb) seeds.push({ idx: ib, type: sb });
    var det = detonate(state, seeds);
    for (var dk2 in det) add(dk2 | 0);
    add(ia); add(ib);
    return set;
  }

  // Clear a set of cells (set them null), tallying per-id clears, nulling any
  // specials, and (optionally) recording each vanished cell for the UI.
  function applyClears(state, set, scoring, step) {
    var cleared = 0;
    for (var key in set) {
      if (!set[key]) continue;
      var i = key | 0;
      var color = state.cells[i];
      if (color == null) continue;
      if (step) step.cleared.push({ idx: i, id: color, special: state.specials[i] || null });
      state.cells[i] = null;
      state.specials[i] = null;
      cleared += 1;
      if (scoring) {
        state.tally[color] = (state.tally[color] || 0) + 1;
        state.clearedTotal += 1;
      }
    }
    return cleared;
  }

  // Gravity: within each column, pull non-null cells (and their specials) down,
  // then refill the emptied top cells from the threaded rng. Optional `step`
  // records falls (a tile sliding down) and spawns (a new tile dropping in).
  function collapseAndRefill(state, step) {
    for (var c = 0; c < state.cols; c += 1) {
      var stackBottom = state.rows - 1;
      for (var r = state.rows - 1; r >= 0; r -= 1) {
        var ri = idx(state, r, c);
        var val = state.cells[ri];
        if (val != null) {
          var sp = state.specials[ri];
          var bi = idx(state, stackBottom, c);
          state.cells[bi] = val;
          state.specials[bi] = sp;
          if (stackBottom !== r) {
            state.cells[ri] = null;
            state.specials[ri] = null;
            if (step) step.falls.push({ col: c, fromRow: r, toRow: stackBottom, id: val, special: sp || null });
          }
          stackBottom -= 1;
        }
      }
      var spawnRank = 0;
      for (var rr = stackBottom; rr >= 0; rr -= 1) {
        var nid = state.gemPool[Math.floor(state.rng() * state.gemPool.length)];
        var ni = idx(state, rr, c);
        state.cells[ni] = nid;
        state.specials[ni] = null;
        if (step) step.spawns.push({ col: c, row: rr, id: nid, rank: spawnRank });
        spawnRank += 1;
      }
    }
  }

  function newStep(chain, effect) {
    return { cleared: [], falls: [], spawns: [], creations: [], gained: 0, chain: chain, effect: effect || null };
  }

  // Resolve the board to a stable state, optionally scoring and minting
  // specials. opts.pivot = [ia,ib] biases special placement to the swapped
  // cell on the first natural cascade. opts.initialClear = a pre-step cleared
  // set (a power-up activation) that fires before normal matching begins.
  // Optional rec ({steps:[]}) records one entry per cascade for the animated UI;
  // it never touches the result or the rng order, so a recorded play and a bare
  // replay are bit-for-bit identical.
  function resolve(state, scoring, rec, opts) {
    opts = opts || {};
    var pivot = opts.pivot || null;
    var chain = 0;

    // Power-up activation pre-step (an explicit swap of a special).
    if (opts.initialClear) {
      chain += 1;
      var before0 = state.score;
      var step0 = rec ? newStep(chain, opts.effect || 'activate') : null;
      var n0 = applyClears(state, opts.initialClear, scoring, step0);
      if (scoring) state.score += n0 * 3 * chain;   // power-ups pay a touch more
      collapseAndRefill(state, step0);
      if (step0) { step0.gained = state.score - before0; rec.steps.push(step0); }
      pivot = null;   // the activation already consumed the swap intent
    }

    while (true) {
      var found = findMatches(state);
      if (!found.runs.length) break;
      chain += 1;
      var before = state.score;
      if (scoring) {
        for (var i = 0; i < found.runs.length; i += 1) {
          var len = found.runs[i].len;
          // base 1/gem, +2/gem beyond 3 (a 5-run pays more per gem), then a
          // chain multiplier for cascades (2nd cascade x2, 3rd x3, ...).
          state.score += (len + Math.max(0, len - 3) * 2) * chain;
        }
      }
      var plan = scoring ? planSpecials(state, found.runs, chain === 1 ? pivot : null)
                         : { creations: [], consumed: {} };
      // hosts transform instead of clearing
      for (var ck in plan.consumed) { if (plan.consumed[ck]) delete found.marked[ck]; }
      var step = rec ? newStep(chain) : null;
      applyClears(state, found.marked, scoring, step);
      for (var m = 0; m < plan.creations.length; m += 1) {
        var cr = plan.creations[m];
        state.specials[cr.idx] = cr.type;
        if (step) step.creations.push({ idx: cr.idx, type: cr.type, id: state.cells[cr.idx] });
      }
      collapseAndRefill(state, step);
      if (step) { step.gained = state.score - before; rec.steps.push(step); }
    }
    ensurePlayable(state, rec);
    return chain;
  }

  // Does the board currently contain any 3+ run? (a settled board should not)
  function hasAnyMatch(state) {
    return findMatches(state).runs.length > 0;
  }

  // Deterministic Fisher-Yates over the flat board, carrying each cell's special
  // with it. Permutes the existing gems only (no add/remove), so the per-id
  // tally and the recommendation are untouched. Consumes the threaded rng, so a
  // reshuffle reproduces identically on replay.
  function shuffleInPlace(state) {
    for (var i = state.cells.length - 1; i > 0; i -= 1) {
      var j = Math.floor(state.rng() * (i + 1));
      var tc = state.cells[i]; state.cells[i] = state.cells[j]; state.cells[j] = tc;
      var ts = state.specials[i]; state.specials[i] = state.specials[j]; state.specials[j] = ts;
    }
  }

  // GUARANTEE A MOVE EXISTS. A match-3 board can refill into a dead state with
  // no legal swap. Any board holding a special always has a move (a special can
  // always be swapped to fire), so this only ever triggers on an all-plain
  // board. When it does, reshuffle the existing gems deterministically until the
  // arrangement has at least one legal move AND no incidental matches (so the
  // player gets a clean, playable board, never a free cascade). Records a single
  // `reshuffle` step for the UI to snap + flourish. Fully deterministic, so
  // seed + move log still reconstruct the round bit-for-bit.
  function ensurePlayable(state, rec) {
    if (legalMoves(state).length > 0) return false;
    var ok = false, guard = 0;
    while (!ok && guard < 400) {
      guard += 1;
      // Almost always a plain reshuffle of the existing gems finds a playable,
      // match-free arrangement (so the per-id tally is preserved). The periodic
      // fillBoard is a safety net for a degenerate multiset (e.g. too few
      // distinct colors left to ever have a move); it repopulates from the full
      // pool. Both paths pull only from the threaded rng, so replay is exact.
      if (guard % 40 === 0) {
        fillBoard(state);
        for (var k = 0; k < state.specials.length; k += 1) state.specials[k] = null;
      } else {
        shuffleInPlace(state);
      }
      ok = !hasAnyMatch(state) && legalMoves(state).length > 0;
    }
    if (rec) rec.steps.push({ type: 'reshuffle', cleared: [], falls: [], spawns: [], creations: [], gained: 0, chain: 0 });
    return true;
  }

  // Are two cells neighbours? Default is the historical 8-neighbour rule
  // (orthogonal OR diagonal). A game created with opts.fourWay restricts to
  // orthogonal only (the fidget game tried this 2026-07-05 and reverted to
  // 8-way the same day, owner call; the flag stays supported). The flag lives
  // on the state so seed + log replays of OLD games stay bit-for-bit identical.
  function adjacent(a, b, fourWay) {
    var dr = Math.abs(a[0] - b[0]), dc = Math.abs(a[1] - b[1]);
    if (fourWay) return dr + dc === 1;
    return dr <= 1 && dc <= 1 && (dr + dc) > 0;
  }

  function swapCells(state, a, b) {
    var ia = idx(state, a[0], a[1]);
    var ib = idx(state, b[0], b[1]);
    var t = state.cells[ia]; state.cells[ia] = state.cells[ib]; state.cells[ib] = t;
    var s = state.specials[ia]; state.specials[ia] = state.specials[ib]; state.specials[ib] = s;
  }

  // Apply a move (swap of two adjacent cells). Three outcomes:
  //   - either cell is a special -> ACTIVATE (always legal; the swap fires the
  //     power-up, even if it forms no colour match)
  //   - otherwise the swap must create a match, else it is reverted and returns
  //     { ok:false } WITHOUT advancing the rng or move counter (replay-exact)
  // Optional `rec` ({steps:[]}) records per-cascade animation steps for the UI;
  // default callers omit it and get the identical result, so determinism holds.
  function applyMove(state, a, b, rec) {
    if (!inBounds(state, a[0], a[1]) || !inBounds(state, b[0], b[1])) return { ok: false, reason: 'oob' };
    if (!adjacent(a, b, state.fourWay)) return { ok: false, reason: 'not-adjacent' };
    var ia = idx(state, a[0], a[1]);
    var ib = idx(state, b[0], b[1]);
    var before = state.score;
    var beforeCleared = state.clearedTotal;

    if (state.specials[ia] || state.specials[ib]) {
      // Power-up activation. Compute the blast from the pre-swap board so a
      // rainbow targets the gem the player dragged it onto.
      var clearSet = activationClears(state, ia, ib);
      var chainA = resolve(state, true, rec, { initialClear: clearSet, effect: activationEffect(state, ia, ib) });
      state.moves += 1;
      state.log.push([a[0], a[1], b[0], b[1]]);
      return { ok: true, activated: true, gained: state.score - before, cleared: state.clearedTotal - beforeCleared, chain: chainA, steps: rec ? rec.steps : null };
    }

    swapCells(state, a, b);
    var found = findMatches(state);
    if (!found.runs.length) {
      swapCells(state, a, b); // revert
      return { ok: false, reason: 'no-match' };
    }
    var chain = resolve(state, true, rec, { pivot: [ia, ib] });
    state.moves += 1;
    state.log.push([a[0], a[1], b[0], b[1]]);
    return { ok: true, gained: state.score - before, cleared: state.clearedTotal - beforeCleared, chain: chain, steps: rec ? rec.steps : null };
  }

  // TIME-UP FLOURISH: detonate every power-up left on the board (chaining
  // through each other like any blast), then resolve the cascades, scoring
  // like an activation. Deterministic and replay-safe: consumes no rng beyond
  // the normal refill stream and records itself in the move log as a
  // ['finale'] marker so replay() reproduces the exact same end state. Does
  // not count as a move. No specials on the board -> { ok: false }, untouched.
  function detonateAll(state, rec) {
    var seeds = [];
    for (var i = 0; i < state.specials.length; i += 1) {
      if (state.specials[i]) seeds.push({ idx: i, type: state.specials[i] });
    }
    if (!seeds.length) return { ok: false, detonated: 0, gained: 0, cleared: 0, chain: 0, steps: rec ? rec.steps : null };
    var before = state.score;
    var beforeCleared = state.clearedTotal;
    var clearSet = detonate(state, seeds);
    var chain = resolve(state, true, rec, { initialClear: clearSet, effect: 'finale' });
    state.log.push(['finale']);
    return { ok: true, detonated: seeds.length, gained: state.score - before, cleared: state.clearedTotal - beforeCleared, chain: chain, steps: rec ? rec.steps : null };
  }

  // A short label describing what an activation does (for the UI burst), read
  // from the pre-swap board.
  function activationEffect(state, ia, ib) {
    var sa = state.specials[ia], sb = state.specials[ib];
    if (sa === SPECIAL.RAINBOW || sb === SPECIAL.RAINBOW) return 'rainbow';
    if (sa && sb) return 'combo';
    var t = sa || sb;
    return isLine(t) ? 'line' : 'bomb';
  }

  // Enumerate every legal move on the current board (deterministic order). The
  // "forward" directions per cell cover every pair exactly once: right + down
  // for a fourWay game, plus down-right + down-left for the historical 8-way
  // rule. A swap touching a special is always legal (it activates). Used by
  // the auto-player + the dead-board guard, so both honour exactly the moves
  // the player is allowed to make.
  var MOVE_DIRS = [[0, 1], [1, 0], [1, 1], [1, -1]];
  var MOVE_DIRS_4 = [[0, 1], [1, 0]];
  function legalMoves(state) {
    var out = [];
    var dirs = state.fourWay ? MOVE_DIRS_4 : MOVE_DIRS;
    for (var r = 0; r < state.rows; r += 1) {
      for (var c = 0; c < state.cols; c += 1) {
        var hi = idx(state, r, c);
        for (var d = 0; d < dirs.length; d += 1) {
          var nr = r + dirs[d][0], nc = c + dirs[d][1];
          if (!inBounds(state, nr, nc)) continue;
          var ni = idx(state, nr, nc);
          if (state.specials[hi] || state.specials[ni] || wouldMatch(state, [r, c], [nr, nc])) {
            out.push({ a: [r, c], b: [nr, nc] });
          }
        }
      }
    }
    return out;
  }

  function wouldMatch(state, a, b) {
    swapCells(state, a, b);
    var found = findMatches(state);
    swapCells(state, a, b);
    return found.runs.length > 0;
  }

  /* ===================================================================== *
   * 4. Deterministic auto-player (tests + CDP harness) + replay
   * ===================================================================== */

  // Plays up to `maxMoves` greedily: each turn picks the move that clears the
  // most gems (special swaps score their full activation; ties broken by the
  // deterministic enumeration order). No randomness beyond the seeded board,
  // so replay(seed, log) === this play.
  function autoPlay(state, maxMoves) {
    maxMoves = maxMoves || 40;
    for (var m = 0; m < maxMoves; m += 1) {
      var moves = legalMoves(state);
      if (!moves.length) break;
      var best = null, bestSize = -1;
      for (var i = 0; i < moves.length; i += 1) {
        var a = moves[i].a, b = moves[i].b;
        var ia = idx(state, a[0], a[1]), ib = idx(state, b[0], b[1]);
        var size;
        if (state.specials[ia] || state.specials[ib]) {
          size = Object.keys(activationClears(state, ia, ib)).length;
        } else {
          swapCells(state, a, b);
          var found = findMatches(state);
          size = 0; for (var key in found.marked) { if (found.marked[key]) size += 1; }
          swapCells(state, a, b);
        }
        if (size > bestSize) { bestSize = size; best = moves[i]; }
      }
      if (!best) break;
      applyMove(state, best.a, best.b);
    }
    return state;
  }

  // Reconstruct a finished game from seed + move log. Returns the settled
  // state; the caller compares .score / .tally against the client's claim.
  // Specials are re-derived from board shape, so the log needs no extra data.
  function replay(opts, log) {
    var state = createGame(opts);
    for (var i = 0; i < log.length; i += 1) {
      var mv = log[i];
      if (mv[0] === 'finale') { detonateAll(state); continue; }
      applyMove(state, [mv[0], mv[1]], [mv[2], mv[3]]);
    }
    return state;
  }

  /* ===================================================================== *
   * 5. Color-harvest -> recipient palette  (THE GATE)
   * ===================================================================== */

  // Turn the per-color clear tally into a recipient palette. This is the
  // make-or-break of the door concept, so the rules are deliberately legible:
  //
  //   1. Candidates are the colors actually cleared (>0). The board is already
  //      the archetype's on-vibe pool, so candidates are on-brand by design.
  //   2. Rank by clears (the EARNED signal). Archetype bias only nudges ties
  //      and barely-cleared colors, so the player's play visibly dominates.
  //   3. Palette size N is earned too: count colors cleared "meaningfully"
  //      (>= a fraction of the top color's clears), clamped to [1, maxColors].
  //   4. Walk the ranked list assigning roles primary/accent/highlight/outline,
  //      skipping any color that is a near-duplicate of one already chosen, and
  //      preferring a high-contrast color for the outline role.
  //   5. Every output id is one of the 12 swatches (candidates come from the
  //      gem pool, which is a subset of the 12), so nothing off-palette can
  //      escape into the reveal or the cart.
  var ROLES = ['primary', 'accent', 'highlight', 'outline'];
  // redmean units; below this two swatches read as redundant on one sign.
  // Calibrated against the 12 real swatches: collapses the genuinely-close
  // pairs (magenta~pink ~108, red~orange ~112) while keeping distinct-but-
  // related ones (cyan~blue ~226) apart.
  var NEAR_DUP_DISTANCE = 118;

  function swatchHex(id) {
    var s = SWATCH_BY_ID[id];
    return s ? s.hex : '#cccccc';
  }

  function buildPalette(opts) {
    opts = opts || {};
    var tally = opts.tally || {};
    var archetype = ARCHETYPES[opts.archetypeId] || ARCHETYPES['neon-maximalist'];
    var maxColors = Math.max(1, Math.min(4, opts.maxColors || 4));
    var biasOrder = archetype.biasOrder || archetype.gemPool;

    // bias score: small, decreasing nudge by archetype rank (first favorite
    // gets the largest nudge). Scaled so it never outweighs a real clear gap.
    var biasOf = {};
    biasOrder.forEach(function (id, i) { biasOf[id] = (biasOrder.length - i); });

    // Build candidate list from cleared colors.
    var candidates = Object.keys(tally)
      .filter(function (id) { return (tally[id] || 0) > 0 && SWATCH_BY_ID[id]; })
      .map(function (id) {
        return { id: id, clears: tally[id], bias: biasOf[id] || 0 };
      });

    // Fallback: nothing cleared (shouldn't happen post-round) -> archetype bias.
    if (!candidates.length) {
      candidates = biasOrder.slice(0, maxColors).map(function (id, i) {
        return { id: id, clears: 0, bias: (biasOrder.length - i) };
      });
    }

    // Rank: clears dominate; bias is a fractional tiebreak (<1 clear of weight)
    // so two colors only reorder on bias when their clears are within ~1.
    var maxBias = biasOrder.length || 1;
    candidates.sort(function (a, b) {
      var sa = a.clears + (a.bias / maxBias) * 0.6;
      var sb = b.clears + (b.bias / maxBias) * 0.6;
      if (sb !== sa) return sb - sa;
      // stable final tiebreak: declared swatch order
      return SWATCHES.findIndex(function (s) { return s.id === a.id; }) -
             SWATCHES.findIndex(function (s) { return s.id === b.id; });
    });

    // Earned palette size: colors cleared >= 25% of the leader count are
    // "meaningful". A one-color masher earns 1; a balanced player earns up to 4.
    var topClears = candidates[0] ? candidates[0].clears : 0;
    var meaningful = candidates.filter(function (c) {
      return topClears === 0 ? true : c.clears >= Math.max(1, topClears * 0.25);
    });
    var targetN = Math.max(1, Math.min(maxColors, meaningful.length || 1));

    // Assign roles with near-dup + contrast guards.
    var chosen = [];
    var chosenHexes = [];
    function tooSimilar(hex) {
      for (var i = 0; i < chosenHexes.length; i += 1) {
        if (colorDistance(hex, chosenHexes[i]) < NEAR_DUP_DISTANCE) return true;
      }
      return false;
    }
    for (var i = 0; i < candidates.length && chosen.length < targetN; i += 1) {
      var cand = candidates[i];
      var hex = swatchHex(cand.id);
      if (tooSimilar(hex)) {
        cand._skippedNearDup = true;
        continue;
      }
      chosen.push(cand);
      chosenHexes.push(hex);
    }
    // If the near-dup guard left fewer distinct colors than the earned count,
    // we DO NOT backfill with a near-duplicate (that would defeat the guard and
    // muddy the sign). A palette of N genuinely distinct earned colors is the
    // honest result; the role list just ends up shorter.

    // Outline role wants the highest-contrast-against-primary color among the
    // chosen set (a sign outline reads best when it pops off the fill). If the
    // current last role color is low-contrast vs primary and a better-contrast
    // chosen color exists, swap it into the outline slot.
    if (chosen.length >= 3) {
      var primaryHex = swatchHex(chosen[0].id);
      var bestIdx = chosen.length - 1;
      var bestContrast = contrastRatio(primaryHex, swatchHex(chosen[bestIdx].id));
      for (var p = 1; p < chosen.length; p += 1) {
        var cr = contrastRatio(primaryHex, swatchHex(chosen[p].id));
        if (cr > bestContrast) { bestContrast = cr; bestIdx = p; }
      }
      if (bestIdx !== chosen.length - 1) {
        var pick = chosen.splice(bestIdx, 1)[0];
        chosen.push(pick);
      }
    }

    var palette = chosen.map(function (c, i) {
      var sw = SWATCH_BY_ID[c.id];
      return {
        role: ROLES[i] || ('extra-' + i),
        id: c.id,
        label: sw.label,
        hex: sw.hex,
        glyph: sw.glyph,
        clears: c.clears
      };
    });

    return {
      archetypeId: archetype.id,
      colors: palette,                       // ordered primary..outline
      colorIds: palette.map(function (p) { return p.id; }),
      single: palette.length === 1,
      // Human-readable, transparent "why" line for the reveal. No em dashes.
      reason: buildReason(palette, archetype)
    };
  }

  function buildReason(palette, archetype) {
    if (!palette.length) return 'We picked an on-brand palette for you.';
    var names = palette.map(function (p) { return p.label; });
    var list;
    if (names.length === 1) list = names[0];
    else if (names.length === 2) list = names[0] + ' and ' + names[1];
    else list = names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
    return 'You cleared ' + list + ' the most, so we made ' + names[0].toLowerCase() +
      ' the lead color of their sign.';
  }

  /* ===================================================================== *
   * Public API
   * ===================================================================== */

  return {
    // config
    SWATCHES: SWATCHES,
    SWATCH_BY_ID: SWATCH_BY_ID,
    ARCHETYPES: ARCHETYPES,
    SPECIAL: SPECIAL,
    isLine: isLine,
    // rng + color math (exported for the UI + tests)
    makeRng: makeRng,
    hashSeed: hashSeed,
    hexToRgb: hexToRgb,
    relLuminance: relLuminance,
    contrastRatio: contrastRatio,
    colorDistance: colorDistance,
    // game
    createGame: createGame,
    findMatches: findMatches,
    legalMoves: legalMoves,
    ensurePlayable: ensurePlayable,
    applyMove: applyMove,
    detonateAll: detonateAll,
    autoPlay: autoPlay,
    replay: replay,
    // palette (the gate)
    buildPalette: buildPalette
  };
});
